import { RefObject, useEffect } from 'react';

/**
 * A standardized hook for handling clicks outside a referenced element
 *
 * @param ref - The React ref to the element that should be monitored for outside clicks
 * @param handler - The function to call when a click outside is detected, receives the event
 * @param isActive - Whether the handler should be active (defaults to true)
 * @param excludeRefs - Array of refs to elements that should be excluded from counting as "outside"
 */
export function useOutsideClickHandler(
  ref: RefObject<HTMLElement>,
  handler: (event: MouseEvent | TouchEvent) => void,
  isActive: boolean = true,
  excludeRefs: RefObject<HTMLElement>[] = []
): void {
  useEffect(() => {
    if (!isActive) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      // Check if the click was inside the ref element
      if (ref.current && ref.current.contains(target)) {
        return;
      }

      // Check if the click was inside any of the excluded elements
      for (const excludeRef of excludeRefs) {
        if (excludeRef.current && excludeRef.current.contains(target)) {
          return;
        }
      }

      handler(event);
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [ref, handler, isActive, excludeRefs]);
}
